import { http } from '@/api/http';

/**
 * Raw transport surface for the multi-tenant OIDC auth admin screens (PTV2-1412): global-plane CRUD
 * over registered OIDC applications ({@code /oidc-credentials}, PTV2-1404) and host→credential auth
 * bindings ({@code /auth-providers}, PTV2-1405). Both require {@code platform:MANAGE}.
 *
 * Hand-written slice DTOs (snake_case, matching the backend {@code @JsonProperty} names) — the
 * api-layer mapper normalises to camelCase view models. The credential's {@code client_id} /
 * {@code client_secret} are write-only: sent on create/update, never returned; {@code has_client_id}
 * / {@code has_secret} tell the UI whether each is stored. A binding holds no secret.
 */

/**
 * The login type (PTV2-1383). Lives on the CREDENTIAL; a provider inherits it. Replaces the former
 * credential `usage` (CLIENT/OPS) and provider `provider_type` (CLIENT/TENANT_OPS/CLUSTER_OPS).
 */
export type CredentialTypeDto = 'CLIENT' | 'TENANT_OPERATOR' | 'CLUSTER_OPERATOR';
export type UnmappedRolePolicyDto = 'FALLBACK_TO_DEFAULTS' | 'DROP' | 'TRUST_CLAIM';
/** (PTV2-1565) Authorization-server profile: a plain OIDC provider, or a SMART-on-FHIR server. */
export type AuthServerProfileDto = 'STANDARD_OIDC' | 'SMART_ON_FHIR';

/** Wire DTO for a registered OIDC application. */
export interface OidcCredentialDto {
  id?: string | null;
  label?: string;
  type?: CredentialTypeDto;
  is_default?: boolean | null;
  issuer_uri?: string;
  well_known_url?: string | null;
  /** (PTV2-1565) Authorization-server profile; STANDARD_OIDC (default) or SMART_ON_FHIR. */
  auth_server_profile?: AuthServerProfileDto | null;
  /** (PTV2-1565) SMART aud (FHIR base URL); used only for a SMART_ON_FHIR profile. */
  fhir_aud?: string | null;
  /** Write-only — never present on reads. */
  client_id?: string | null;
  /** Write-only — never present on reads. */
  client_secret?: string | null;
  /** Read-only — whether an encrypted client id is stored. */
  has_client_id?: boolean | null;
  /** Read-only — whether an encrypted client secret is stored. */
  has_secret?: boolean | null;
  scopes?: string | null;
  /** Read-only — a synthetic, non-editable built-in entry (e.g. the global bootstrap IdP). */
  read_only?: boolean | null;
}

/**
 * Request body for the one-click "local application" create (POST /oidc-credentials/local). The admin
 * sends only these fields; the backend fills issuer + client id + secret from the cluster's bootstrap
 * IdP config.
 */
export interface LocalOidcCredentialRequestDto {
  label: string;
  type: CredentialTypeDto;
  is_default?: boolean | null;
  scopes?: string | null;
}

/** Whether one-click local-application registration is offered in this environment. */
export interface LocalOidcAvailabilityDto {
  available?: boolean;
}

/** Result of an OIDC-credential connectivity probe (discovery + JWKS). Mirrors the email-server status. */
export interface OidcCredentialStatusDto {
  ok: boolean;
  issuer?: string | null;
  error_code?: string | null;
  detail?: string | null;
}

/** One external→internal role-map entry for an auth provider. */
export interface AuthProviderRoleMappingDto {
  external_role: string;
  internal_role: string;
}

/** Wire DTO for a {@code tenant_auth_provider} host→credential binding. Holds no secret. */
export interface AuthProviderDto {
  id?: string | null;
  host?: string;
  tenant_uuid?: string | null;
  /** Derived, read-only: inherited from the bound credential (PTV2-1383). Ignored on writes. */
  type?: CredentialTypeDto;
  credential_id?: string;
  tenant_id_claim_key?: string | null;
  /**
   * (PTV2-1520) External IdP tenant/org id. When set, the token's tenant_id claim is matched against
   * this instead of the internal tenant_uuid. Blank/absent => match the internal UUID.
   */
  external_tenant_id?: string | null;
  /**
   * (PTV2-1520) When false, the tenant-id claim assertion is skipped entirely. Absent => true (default
   * on).
   */
  tenant_claim_check_enabled?: boolean | null;
  user_id_claim_key?: string;
  roles_claim_key?: string | null;
  roles_from_claim_enabled?: boolean | null;
  unmapped_role_policy?: UnmappedRolePolicyDto | null;
  userinfo_enabled?: boolean | null;
  /** (PTV2-1523) Claim mapped to the user's full display name; wins over joined first/last. */
  claim_name?: string | null;
  claim_first_name?: string | null;
  claim_last_name?: string | null;
  claim_email?: string | null;
  jit_provisioning_enabled?: boolean | null;
  /** (PTV2-1425) JIT-created users are ACTIVE immediately instead of PENDING_ACTIVATION. */
  new_user_accounts_are_active?: boolean | null;
  enabled?: boolean | null;
  /** Read-only — a synthetic, non-editable built-in binding (e.g. the global bootstrap IdP). */
  read_only?: boolean | null;
  role_mappings?: AuthProviderRoleMappingDto[] | null;
  default_roles?: string[] | null;
}

function encode(id: string): string {
  return encodeURIComponent(id);
}

export const adminAuthEndpoints = {
  /** GET /oidc-credentials — full list (global plane, not paginated). */
  getOidcCredentials: (options?: { signal?: AbortSignal }): Promise<OidcCredentialDto[]> =>
    http.get<OidcCredentialDto[]>('/oidc-credentials', options),

  /** POST /oidc-credentials — create. Returns 201 with the stored row (secrets stripped). */
  createOidcCredential: (
    body: OidcCredentialDto,
    options?: { signal?: AbortSignal },
  ): Promise<OidcCredentialDto> => http.post<OidcCredentialDto>('/oidc-credentials', body, options),

  /** POST /oidc-credentials/local — one-click local-application create (backend fills issuer/client). */
  createLocalOidcCredential: (
    body: LocalOidcCredentialRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<OidcCredentialDto> =>
    http.post<OidcCredentialDto>('/oidc-credentials/local', body, options),

  /** GET /oidc-credentials/local-availability — whether the "Add local application" button applies. */
  getLocalOidcAvailability: (options?: { signal?: AbortSignal }): Promise<LocalOidcAvailabilityDto> =>
    http.get<LocalOidcAvailabilityDto>('/oidc-credentials/local-availability', options),

  /** GET /oidc-credentials/{id}/status — probe the issuer (OIDC discovery + JWKS; no login performed). */
  getOidcCredentialStatus: (
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<OidcCredentialStatusDto> =>
    http.get<OidcCredentialStatusDto>(`/oidc-credentials/${encode(id)}/status`, options),

  /** PUT /oidc-credentials/{id} — update. A blank secret keeps the stored one. */
  updateOidcCredential: (
    id: string,
    body: OidcCredentialDto,
    options?: { signal?: AbortSignal },
  ): Promise<OidcCredentialDto> =>
    http.put<OidcCredentialDto>(`/oidc-credentials/${encode(id)}`, body, options),

  /** DELETE /oidc-credentials/{id}. */
  deleteOidcCredential: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/oidc-credentials/${encode(id)}`, options),

  /** GET /auth-providers — full list (global plane, not paginated). */
  getAuthProviders: (options?: { signal?: AbortSignal }): Promise<AuthProviderDto[]> =>
    http.get<AuthProviderDto[]>('/auth-providers', options),

  /** GET /auth-providers/{id} — single binding (drives the detail page). */
  getAuthProvider: (id: string, options?: { signal?: AbortSignal }): Promise<AuthProviderDto> =>
    http.get<AuthProviderDto>(`/auth-providers/${encode(id)}`, options),

  /** POST /auth-providers — create. Returns 201 with the stored binding. */
  createAuthProvider: (
    body: AuthProviderDto,
    options?: { signal?: AbortSignal },
  ): Promise<AuthProviderDto> => http.post<AuthProviderDto>('/auth-providers', body, options),

  /** PUT /auth-providers/{id} — update. */
  updateAuthProvider: (
    id: string,
    body: AuthProviderDto,
    options?: { signal?: AbortSignal },
  ): Promise<AuthProviderDto> =>
    http.put<AuthProviderDto>(`/auth-providers/${encode(id)}`, body, options),

  /** DELETE /auth-providers/{id}. */
  deleteAuthProvider: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/auth-providers/${encode(id)}`, options),
};
